// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { NumberTextField } from '@/components/number-text-field';
import {
  Delete as DeleteIcon,
  InventoryOutlined,
} from '@/constants/hardfin-standard-icons';
import { ItemBomLineItem } from '@/items/api/types';
import { BOMItemSearch } from '@/items/components/bom-item-search';
import { SparseItem } from '@/sparse/api/types';
import { Box, Card, IconButton, Stack, Typography } from '@mui/material';
import { Ref, forwardRef, useImperativeHandle } from 'react';
import {
  Controller,
  FieldValues,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';

/**
 * Props for ItemBOMForm component.
 * The component uses useFormContext to get the form control,
 * so it must be wrapped in a FormProvider.
 */
interface Props {
  /**
   * The name of the bom field in the form.
   * The field at this path should be an array of ItemBomLineItem objects.
   */
  name: string;

  /**
   * Whether the form is disabled.
   */
  disabled?: boolean;
}

export interface ItemBOMFormRef {
  /**
   * Adds a new line item to the BOM form.
   * Creates a new line item with default values.
   */
  addLineItem: () => void;
}

/**
 * Inner implementation of ItemBOMForm.
 * Uses useFormContext to get the form control from React context.
 */
function ItemBOMFormInner(props: Props, ref: Ref<ItemBOMFormRef>) {
  const { name, disabled = false } = props;

  // Get the form control from context - requires FormProvider wrapper
  const { control } = useFormContext<FieldValues>();

  // Watch the bom field to get currently selected items
  const watchedValue = useWatch({ control, name });
  const bomItems: ItemBomLineItem[] = Array.isArray(watchedValue)
    ? watchedValue
    : [];
  const selectedItems = bomItems
    .map((lineItem: ItemBomLineItem) => lineItem.item)
    .filter((item: SparseItem | null): item is SparseItem => item !== null);

  const { fields, remove, append } = useFieldArray({ control, name });

  useImperativeHandle(ref, () => ({
    addLineItem: () => {
      append({ item: null, quantity: 1 });
    },
  }));

  return (
    <Stack spacing={2} marginY={1} marginLeft={2} marginRight={1}>
      {fields.length === 0 ? (
        <Card
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 4,
            textAlign: 'center',
            minHeight: '200px',
          }}
        >
          <InventoryOutlined
            sx={{ fontSize: 48, color: 'text.secondary', marginBottom: 2 }}
          />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No items added yet
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: '400px', marginBottom: 3 }}
          >
            Add items to build your bill of materials
          </Typography>
        </Card>
      ) : (
        fields.map((field, index) => {
          return (
            <Stack
              key={field.id}
              direction="row"
              spacing={2}
              alignItems="center"
              width="100%"
            >
              <Box sx={{ flexGrow: 10, minWidth: 0 }}>
                <Controller
                  control={control}
                  name={`${name}.${index}.item`}
                  render={({ field: itemField, fieldState }) => (
                    <BOMItemSearch
                      label="Item"
                      value={
                        itemField.value === undefined ? null : itemField.value
                      }
                      onChange={(value) => {
                        itemField.onChange(value);
                      }}
                      disabled={disabled}
                      hiddenItems={selectedItems}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flexGrow: 2, minWidth: 0 }}>
                <Controller
                  control={control}
                  name={`${name}.${index}.quantity`}
                  render={({ field: quantityField, fieldState }) => (
                    <NumberTextField
                      label="Quantity"
                      value={quantityField.value}
                      onChange={(values) => {
                        quantityField.onChange(
                          values.value === '' ? null : values.floatValue
                        );
                      }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      disabled={disabled}
                      allowZero={true}
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <IconButton
                  onClick={() => remove(index)}
                  disabled={disabled}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Stack>
          );
        })
      )}
    </Stack>
  );
}

/**
 * A form component for managing an item's bill of materials.
 * Allows adding, removing, and editing items and quantities.
 *
 * IMPORTANT: This component must be wrapped in a FormProvider from react-hook-form.
 * It uses useFormContext internally to access the form control.
 *
 * Features:
 * - Dynamic addition and removal of BOM items
 * - Item selection with autocomplete (only BULK items)
 * - Quantity input with validation
 * - Mutually exclusive item selection (items already selected are hidden from dropdowns)
 *
 * @example
 * ```tsx
 * const methods = useForm({ ... });
 * return (
 *   <FormProvider {...methods}>
 *     <ItemBOMForm name="bom" />
 *   </FormProvider>
 * );
 * ```
 */
export const ItemBOMForm = forwardRef(ItemBOMFormInner);
